import { createService } from "../../factory/service-factory.ts";
import {
  IRequestSetup,
  IUser,
  IUserEquipament,
  IUserPix,
  SearchUserResponse,
  UpdateUser,
} from "../interfaces/IUser.ts";
import { BaseResponse } from "@/integration/application/interfaces/base.ts";
import { TeamUser } from "@integration/team/interfaces/ITeam.ts";
import { ISearchPagination } from "@/integration/application/interfaces/searchPagination.interface.ts";
import { IRecomendData } from "@/integration/user/interfaces/IRecomendData.interface.ts";

/**
 * Service for user-related API calls
 * Refactored to use the service-factory for better error handling and request cancellation
 */
export default function userService() {
  // Create base services for user endpoints
  const userService = createService("/user");
  const publicUserService = createService("/public/user");
  const recommendationsService = createService("/recommendations");

  const getMe = async (): Promise<BaseResponse<IUser>> => {
    return await userService.customGet<BaseResponse<IUser>>("/profile/me");
  };

  const getUserByIdPublic = async (
    userId: string,
  ): Promise<BaseResponse<IUser>> => {
    return await publicUserService.getById<BaseResponse<IUser>>(userId);
  };

  const getSearchUser = async (
    name?: string,
  ): Promise<BaseResponse<SearchUserResponse>[]> => {
    return await publicUserService.customGet<BaseResponse<SearchUserResponse>[]>(
      "/options/all", { params: { name } }
    );
  };

  const putProfile = async (userId: string, updateUser: UpdateUser) => {
    return await userService.update(userId, {id: userId, ...updateUser});
  };

  const getTeamsUserById = async (
    id: string,
  ): Promise<BaseResponse<TeamUser>> => {
    return await userService.customGet<BaseResponse<TeamUser>>(`/${id}/teams`);
  };

  const postSetupUser = async (userId: string, data: IRequestSetup) => {
    return await userService.customPost(`/${userId}/equipament`, data);
  };

  const putSetupUser = async (
    userId: string,
    equipamentId: string,
    data: IRequestSetup,
  ) => {
    return await userService.customPut(`/${userId}/equipament/${equipamentId}/update`, data);
  };

  const getSetupByUserIdPublic = async (
    userId: string,
  ): Promise<BaseResponse<IUserEquipament>[]> => {
    return await publicUserService.customGet<BaseResponse<IUserEquipament>[]>(`/${userId}/equipament`);
  };

  const getUserNickNamePublic = async (
    nickname: string,
  ): Promise<BaseResponse<IUser>[]> => {
    return await publicUserService.customGet<BaseResponse<IUser>[]>(`/pro/${nickname}`);
  };

  const deleteSetup = async (
    userId: string,
    equipamentId: string,
  ): Promise<void> => {
    return await userService.customDelete(`/${userId}/equipament/${equipamentId}`);
  };

  const getUsersSearchPagination = async (searchPagination: ISearchPagination
  ): Promise<BaseResponse<IUserEquipament>[]> => {
    const params = Object.entries(searchPagination)
      .filter(([_, value]) => value != null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    return await publicUserService.customGet<BaseResponse<IUserEquipament>[]>("/options/search/", { params });
  };

  const getPixKeysByUserId = async (
    userId: string,
  ): Promise<IUserPix[]> => {
    return await userService.customGet<IUserPix[]>(`/${userId}/pix/all`);
  };

  const postPixKey = async (userId: string, data: Omit<IUserPix, "id" | "created_at" | "updated_at">) => {
    return await userService.customPost(`/${userId}/pix`, { ...data, user_id: userId });
  };

  const putPixKey = async (
    userId: string,
    pixKeyId: string,
    data: Omit<IUserPix, "user_id">,
  ): Promise<IUserPix> => {
    return await userService.customPut<IUserPix>(`/${userId}/pix/${pixKeyId}`, data);
  };

  const deletePixKey = async (
    userId: string,
    pixKeyId: string,
  ): Promise<void> => {
    return await userService.customDelete(`/${userId}/pix/${pixKeyId}`);
  };

  const getReComendations = async (): Promise<IRecomendData> => {
    return await recommendationsService.getAll<IRecomendData>();
  };

  return {
    getMe,
    getUserByIdPublic,
    getSearchUser,
    putProfile,
    getTeamsUserById,
    postSetupUser,
    putSetupUser,
    deleteSetup,
    getSetupByUserIdPublic,
    getUsersSearchPagination,
    getPixKeysByUserId,
    postPixKey,
    putPixKey,
    deletePixKey,
    getReComendations,
    getUserNickNamePublic
  };
}
